/**
 * What each Azure Storage form control WRITES (spec §4.1): the engine's
 * first-visit defaults, the three "Connect using" modes including the Azurite
 * preset, and what a typed or pasted server value implies about scope,
 * endpoints and services.
 *
 * Split from `azureStorageFormRules.ts` (which only READS the values) so both
 * stay inside the 200-line rule.
 */
import type { AzureStorageEndpoints } from '@/types/azureStorage';
import { storageServiceOf } from '@/types/azureStorage';
import {
  AZURITE_DEFAULT_ACCOUNT,
  AZURITE_DEFAULT_HOST,
  AZURITE_DEFAULT_PORTS,
  AZURITE_WELL_KNOWN_KEY,
  normalizeStorageAccountInput,
} from '@/core/engines/azureStorageUrl';
import { parseAzureStorageConnectionString } from '@/core/engines/azureStorageConnectionString';
import type { ConnectionFormValues } from './connectionFormSchema';
import { AZURITE_SERVICES, storageConnectMode, type StorageConnectMode } from './azureStorageFormRules';
import { sharePathFormValues } from './azureStorageSharePath';

type StorageFormValues = Partial<ConnectionFormValues>;

/**
 * The Azure Storage draft's first-visit defaults (`engineDefaultValues`, C0). Leaving the engine needs no clearing any
 * more: every engine has its own draft, and a first visit elsewhere starts from that engine's defaults.
 */
export function azureStorageEngineDefaults(): StorageFormValues {
  return {
    authentication: 'Account Key',
    port: '0',
    login: '',
    password: '',
    database: '',
    ssl: false,
    entraTenantId: '',
    azureStorageEndpoints: undefined,
    // undefined means "every service" (spec §4.5) — an explicit list is only
    // written once the user unticks one.
    azureStorageServices: undefined,
    azureStorageScope: { kind: 'account' },
    azureStorageEmulator: false,
    azureResourceId: undefined,
    azureStorageShareChoice: undefined,
  };
}

function azuriteEndpoints(): AzureStorageEndpoints {
  return {
    blob: `http://${AZURITE_DEFAULT_HOST}:${AZURITE_DEFAULT_PORTS.blob}/${AZURITE_DEFAULT_ACCOUNT}`,
    queue: `http://${AZURITE_DEFAULT_HOST}:${AZURITE_DEFAULT_PORTS.queue}/${AZURITE_DEFAULT_ACCOUNT}`,
    table: `http://${AZURITE_DEFAULT_HOST}:${AZURITE_DEFAULT_PORTS.table}/${AZURITE_DEFAULT_ACCOUNT}`,
  };
}

/** What picking one of the three "Connect using" modes writes into the form. */
export function storageModeValues(mode: StorageConnectMode): StorageFormValues {
  if (mode === 'emulator') {
    return {
      server: AZURITE_DEFAULT_ACCOUNT,
      authentication: 'Account Key',
      // Published in the Azurite README — a well-known constant, not a secret.
      password: AZURITE_WELL_KNOWN_KEY,
      azureStorageEndpoints: azuriteEndpoints(),
      azureStorageServices: [...AZURITE_SERVICES],
      azureStorageScope: { kind: 'account' },
      azureStorageEmulator: true,
      azureStorageShareChoice: undefined,
    };
  }
  if (mode === 'resource') {
    return {
      azureStorageEmulator: false,
      // The pasted resource URL decides the scope, endpoint and service.
      azureStorageScope: undefined,
      azureStorageEndpoints: undefined,
      azureStorageServices: undefined,
      azureStorageShareChoice: undefined,
    };
  }
  return {
    server: '',
    password: '',
    azureStorageEndpoints: undefined,
    azureStorageServices: undefined,
    azureStorageScope: { kind: 'account' },
    azureStorageEmulator: false,
    azureStorageShareChoice: undefined,
  };
}

/**
 * What the "Account name or endpoint URL" value implies once the user leaves the
 * field: a bare account name / account endpoint is account scope; a container,
 * blob, table, queue or share URL is a single-resource attachment whose endpoint
 * and service come from the URL, and whose SAS query becomes the secret.
 */
export function storageServerBlurValues(input: string, current?: StorageFormValues): StorageFormValues {
  const trimmed = input.trim();
  if (!trimmed) return {};

  const parsed = parseAzureStorageConnectionString(trimmed);
  if (parsed && (parsed.kind === 'sas-url' || parsed.kind === 'public-url')) {
    const service = storageServiceOf(parsed.resource);
    const values: StorageFormValues = {
      // A share folder keeps its trailing '/', so a later blur of the same text still reads as a folder (S7-10).
      server: parsed.endpoint + resourcePathOf(trimmed, parsed.endpoint) + (parsed.resource.kind === 'directory' ? '/' : ''),
      ...sharePathFormValues(parsed, current),
      azureStorageEndpoints: service ? { [service]: parsed.endpoint } : undefined,
      azureStorageServices: service ? [service] : undefined,
    };
    if (parsed.kind === 'sas-url') {
      values.authentication = 'Shared Access Signature';
      values.password = parsed.sas;
    }
    return withPickSettled(values, current);
  }

  const normalized = normalizeStorageAccountInput(trimmed);
  const server = normalized.accountName ?? normalized.endpoint;
  return server ? withPickSettled({ server, azureStorageScope: { kind: 'account' } }, current) : {};
}

/**
 * A blur that moves the values to another mode than the one they read right after the user's "Connect using" pick hands
 * the choice back to the values (the pick is cleared, as the pick's own component state used to reset — C3 made it a
 * value); a blur that leaves them where the pick found them keeps it.
 */
function withPickSettled(written: StorageFormValues, current: StorageFormValues | undefined): StorageFormValues {
  const pick = current?.azureStorageModePick;
  if (!pick || storageConnectMode({ ...current, ...written }) === pick.derivedAtPick) return written;
  return { ...written, azureStorageModePick: undefined };
}

/** The resource path of a pasted URL, minus its SAS query (which moves to `password`). */
function resourcePathOf(raw: string, endpoint: string): string {
  const withoutQuery = raw.split('?')[0].replace(/\/+$/, '');
  return withoutQuery.startsWith(endpoint) ? withoutQuery.slice(endpoint.length) : '';
}
